// Realtime-менеджер: STOMP over WebSocket; при недоступности WS — REST-fallback
// отправки + polling /sync (общий updates-лог, конвергенция с WS).
import { accessExpiresSoon } from './lib/accessTokenExpiry';
import { diag } from './lib/diagLog';
import { Client, IMessage, StompSubscription } from '@stomp/stompjs';
import { getAccessToken, getAuthSession, tryRefresh } from './api/client';
import { outbox } from './lib/outbox';
import { applyBeforeCursor, applyEventsIndividually, BoundedSeenSet, syncRetryDelayMs } from './lib/realtimeState';
import type { AckMessage, MessageDto, MessageSend } from './api/types';

type MessageHandler = (msg: MessageDto) => void;
type AckHandler = (ack: AckMessage) => void;
type EventHandler = (chatId: string, ev: unknown) => void;
type RtcHandler = (chatId: string, sig: unknown) => void;
type UserHandler = (ev: unknown) => void;
type StatusHandler = (connected: boolean) => void;

/** Ответ GET /sync: hasMore — читать следующую пачку сразу; resync — курсор устарел, нужна полная сверка. */
type SyncEvent = { type?: string; message?: MessageDto; chat_id?: string; [k: string]: unknown };
type SyncPage = { cursor: number; events: SyncEvent[]; hasMore?: boolean; resync?: boolean };
/** Предел пачек за один тик polling (100 событий каждая). */
const MAX_SYNC_BATCHES = 20;

class Realtime {
  private client: Client | null = null;
  private connected = false;
  private msgHandlers = new Set<MessageHandler>();
  private ackHandlers = new Set<AckHandler>();
  private evtHandlers = new Set<EventHandler>();
  private rtcHandlers = new Set<RtcHandler>();
  private userHandlers = new Set<UserHandler>();
  private statusHandlers = new Set<StatusHandler>();
  private syncHandlers = new Set<StatusHandler>();
  private resyncHandlers = new Set<(signal: AbortSignal) => void | Promise<void>>();
  private syncing = false;
  // Все чаты пользователя наблюдаются целиком (messages+events+rtc) — иначе звонки
  // и уведомления из неоткрытых чатов не долетают по WS. Переживает переподключение.
  private chatSubs = new Set<string>();
  // Живые объекты STOMP-подписок для наблюдаемых чатов — нужны, чтобы unwatchChat мог
  // реально отписаться (client.subscribe() без сохранения возврата отписать нельзя),
  // иначе повторный watchChat/unwatchChat/watchChat породит вторую подписку поверх первой
  // и события будут приходить продублированными.
  private chatSubscriptions = new Map<string, { msg: StompSubscription; evt: StompSubscription; rtc: StompSubscription }>();
  private pollTimer: number | null = null;
  private tokenTimer: number | null = null;
  private tokenTimerGeneration = 0;
  private fallbackPollGeneration: number | null = null;
  private cursor = 0;
  private everConnected = false;
  private generation = 0;
  private lifecycleAbort: AbortController | null = null;
  private seedAbort: AbortController | null = null;
  private pollAbort: AbortController | null = null;
  private pollInFlight = false;
  private pollInFlightGeneration: number | null = null;
  private readonly seenMessageIds = new BoundedSeenSet<number>();
  private readonly seenEventBodies = new BoundedSeenSet<string>();

  /** Подключиться (после логина). */
  connect() {
    if (this.client) return;
    const generation = ++this.generation;
    this.lifecycleAbort = new AbortController();
    this.cursor = 0;
    // watchChat() may have run before connect() (initial chat refresh). Keep those IDs;
    // onConnect will create their subscriptions once the STOMP connection is ready.
    this.everConnected = false;
    this.pollInFlight = false;
    this.seenMessageIds.clear();
    this.seenEventBodies.clear();
    const seed = this.seedCursor(generation);
    const client = new Client({
      brokerURL: (location.protocol === 'https:' ? 'wss://' : 'ws://') + location.host + '/ws',
      connectHeaders: { Authorization: 'Bearer ' + (getAccessToken() ?? ''), 'x-rtc-proto': '2' },
      reconnectDelay: 5000,
      // STOMP heart-beat явно (у stompjs по умолчанию 10 с) — в унисон с сервером и KMP-клиентом.
      heartbeatIncoming: 25000,
      heartbeatOutgoing: 25000,
      // connectHeaders вычисляется один раз при создании Client — без этого хука
      // каждая попытка переподключения после долгого простоя слала бы истёкший токен.
      beforeConnect: async () => {
        if (!this.isCurrent(client, generation)) return;
        if (accessExpiresSoon(getAccessToken())) await tryRefresh().catch(() => {});
        if (this.isCurrent(client, generation)) {
          client.connectHeaders = { Authorization: 'Bearer ' + (getAccessToken() ?? ''), 'x-rtc-proto': '2' };
        }
      },
      onConnect: () => {
        if (!this.isCurrent(client, generation)) return;
        const wasReconnect = this.everConnected;
        this.everConnected = true;
        this.setConnected(true);
        this.startTokenTimer(client, generation);
        this.stopPolling();
        // Сначала устанавливаем подписки, затем догоняем cursor: так нет окна между
        // catch-up и SUBSCRIBE; перекрытие закрывается синхронной дедупликацией ниже.
        client.subscribe('/user/queue/acks', (m) => this.dispatchAck(m));
        client.subscribe('/user/queue/updates', (m) => this.dispatchUser(m));
        client.subscribe('/user/queue/rtc', (m) => this.dispatchUserRtc(m));
        for (const chatId of this.chatSubs) this.subscribeChat(chatId);
        if (wasReconnect) {
          this.setSyncing(true);
          diag({ kind: 'conn', what: 'sync:catchup' });
        }
        void seed.then(async (seeded) => {
          if (!this.isCurrent(client, generation)) return;
          if (!wasReconnect && !seeded) await this.seedCursor(generation);
          let syncFailures = 0;
          while (this.isCurrent(client, generation) && this.connected) {
            if (await this.poll(generation)) break;
            await new Promise<void>((resolve) => window.setTimeout(resolve, syncRetryDelayMs(syncFailures++)));
          }
        }).finally(() => {
          if (wasReconnect && this.isCurrent(client, generation)) this.setSyncing(false);
        });
      },
      onStompError: () => this.fallback(client, generation),
      onWebSocketClose: () => this.fallback(client, generation),
    });
    this.client = client;
    client.activate();
  }

  disconnect() {
    const client = this.client;
    ++this.generation;
    this.stopTokenTimer();
    this.client = null;
    this.lifecycleAbort?.abort();
    this.lifecycleAbort = null;
    this.seedAbort?.abort();
    this.seedAbort = null;
    this.pollAbort?.abort();
    this.pollAbort = null;
    this.pollInFlight = false;
    this.pollInFlightGeneration = null;
    this.cursor = 0;
    this.chatSubs.clear();
    this.chatSubscriptions.clear();
    this.seenMessageIds.clear();
    this.seenEventBodies.clear();
    outbox.stop();
    this.setSyncing(false);
    this.setConnected(false);
    this.everConnected = false;
    this.stopPolling();
    if (client) void client.deactivate().catch(() => {});
  }

  onMessage(h: MessageHandler) {
    this.msgHandlers.add(h);
    return () => { this.msgHandlers.delete(h); };
  }

  onAck(h: AckHandler) {
    this.ackHandlers.add(h);
    return () => { this.ackHandlers.delete(h); };
  }

  /** Событие чата (typing, read, member_added/removed, edited, deleted) — для ВСЕХ наблюдаемых чатов. */
  onEvent(h: EventHandler) {
    this.evtHandlers.add(h);
    return () => { this.evtHandlers.delete(h); };
  }

  /** RTC-сигналинг звонков — для ВСЕХ наблюдаемых чатов (входящий звонок может быть из любого). */
  onRtc(h: RtcHandler) {
    this.rtcHandlers.add(h);
    return () => { this.rtcHandlers.delete(h); };
  }

  /** Персональные события, не привязанные к чату (например, смена роли админом). */
  onUser(h: UserHandler) {
    this.userHandlers.add(h);
    return () => { this.userHandlers.delete(h); };
  }

  /** Статус соединения (WS живой / упали на polling-фоллбэк) — для индикатора "Соединение…" в UI. */
  onStatus(h: StatusHandler) {
    this.statusHandlers.add(h);
    return () => { this.statusHandlers.delete(h); };
  }

  isConnected(): boolean {
    return this.connected;
  }

  /** Идёт догоняющая сверка через /sync после переподключения («Обновление…» в UI). */
  isSyncing(): boolean {
    return this.syncing;
  }

  onSyncing(h: StatusHandler) {
    this.syncHandlers.add(h);
    return () => { this.syncHandlers.delete(h); };
  }

  /** Сервер сообщил, что лог /sync обрезан и курсор устарел — нужна полная сверка (список чатов, открытая лента). */
  onResync(h: (signal: AbortSignal) => void | Promise<void>) {
    this.resyncHandlers.add(h);
    return () => { this.resyncHandlers.delete(h); };
  }

  private setSyncing(v: boolean) {
    if (this.syncing === v) return;
    this.syncing = v;
    for (const h of this.syncHandlers) h(v);
  }

  /** Начать наблюдение за чатом: подписка на messages+events+rtc topic'и (сразу или при коннекте). */
  watchChat(chatId: string) {
    if (this.chatSubs.has(chatId)) return;
    this.chatSubs.add(chatId);
    this.subscribeChat(chatId);
  }

  /** Чаты, на которые сейчас оформлена подписка (в т.ч. отложенная до коннекта). */
  watchedChatIds(): string[] {
    return [...this.chatSubs];
  }

  unwatchChat(chatId: string) {
    this.chatSubs.delete(chatId);
    const subs = this.chatSubscriptions.get(chatId);
    if (subs) {
      subs.msg.unsubscribe();
      subs.evt.unsubscribe();
      subs.rtc.unsubscribe();
      this.chatSubscriptions.delete(chatId);
    }
  }

  private subscribeChat(chatId: string) {
    if (!this.connected || !this.client) return;
    const msg = this.client.subscribe('/topic/chats/' + chatId + '/messages', (m) => this.dispatchMsg(m));
    const evt = this.client.subscribe('/topic/chats/' + chatId + '/events', (m) => this.dispatchEvt(chatId, m));
    const rtc = this.client.subscribe('/topic/chats/' + chatId + '/rtc', (m) => this.dispatchRtc(chatId, m));
    this.chatSubscriptions.set(chatId, { msg, evt, rtc });
  }

  /** Сохранить сообщение в очередь; доставка через REST идёт независимо от экрана.
   *  Promise означает commit IndexedDB, серверный ответ приходит через onMessage. */
  async send(chatId: string, body: MessageSend, session = getAuthSession()): Promise<void> {
    await outbox.enqueue(chatId, body, session);
  }

  startOutbox(ownerId: string) {
    outbox.start(ownerId, message => this.dispatchMessageDto(message));
  }

  /** Публикация в /app (для RTC-сигналинга). */
  publish(dest: string, body: unknown): boolean {
    if (this.connected && this.client) {
      this.client.publish({ destination: dest, body: JSON.stringify(body) });
      return true;
    }
    return false;
  }

  // ---- внутреннее ----

  private dispatchMsg(msg: IMessage) {
    try {
      this.dispatchMessageDto(JSON.parse(msg.body) as MessageDto);
    } catch {
      /* битый фрейм */
    }
  }

  private dispatchMessageDto(dto: MessageDto) {
    if (!this.acceptMessage(dto)) return;
    try {
      for (const h of this.msgHandlers) h(dto);
    } catch (error) {
      if (dto.id > 0) this.seenMessageIds.delete(dto.id);
      throw error;
    }
  }

  private acceptMessage(dto: MessageDto): boolean {
    if (dto.id <= 0) return true;
    if (this.seenMessageIds.has(dto.id)) return false;
    this.seenMessageIds.add(dto.id);
    return true;
  }

  private eventFingerprint(ev: unknown, chatId?: string): string {
    const record = ev && typeof ev === 'object' ? ev as Record<string, unknown> : null;
    const eventChatId = chatId ?? (typeof record?.chat_id === 'string' ? record.chat_id : '');
    const normalized = record && 'chat_id' in record
      ? Object.fromEntries(Object.entries(record).filter(([key]) => key !== 'chat_id'))
      : ev;
    return `${eventChatId}:${JSON.stringify(normalized) ?? String(normalized)}`;
  }

  private acceptEvent(ev: unknown, chatId?: string): boolean {
    const fingerprint = this.eventFingerprint(ev, chatId);
    if (this.seenEventBodies.has(fingerprint)) return false;
    this.seenEventBodies.add(fingerprint);
    return true;
  }

  private dispatchEventOnce(ev: unknown, deliver: () => void, chatId?: string) {
    const fingerprint = this.eventFingerprint(ev, chatId);
    if (!this.acceptEvent(ev, chatId)) return;
    try {
      deliver();
    } catch (error) {
      this.seenEventBodies.delete(fingerprint);
      throw error;
    }
  }

  private dispatchEvt(chatId: string, msg: IMessage) {
    try {
      const ev = JSON.parse(msg.body);
      this.dispatchEventOnce(ev, () => {
        for (const h of this.evtHandlers) h(chatId, ev);
      }, chatId);
    } catch {
      /* битый фрейм или отказ обработчика; /sync изолирует ошибку события и продолжит пачку */
    }
  }

  private dispatchRtc(chatId: string, msg: IMessage) {
    try {
      const sig = JSON.parse(msg.body);
      for (const h of this.rtcHandlers) h(chatId, sig);
    } catch {
      /* битый фрейм */
    }
  }

  private dispatchUserRtc(msg: IMessage) {
    try {
      const sig = JSON.parse(msg.body) as { chatId?: string };
      if (!sig.chatId) return;
      for (const h of this.rtcHandlers) h(sig.chatId, sig);
    } catch {
      /* битый фрейм */
    }
  }

  private dispatchAck(msg: IMessage) {
    try {
      const ack = JSON.parse(msg.body) as AckMessage;
      for (const h of this.ackHandlers) h(ack);
    } catch {
      /* ignore */
    }
  }

  private dispatchUser(msg: IMessage) {
    try {
      const ev = JSON.parse(msg.body);
      this.dispatchEventOnce(ev, () => {
        // Своё «read» при выключенных статусах прочтения приходит личной очередью (чужим его не
        // раскрывают) — для клиента это то же событие чата.
        if (ev.type === 'read' && ev.chat_id) {
          for (const h of this.evtHandlers) h(ev.chat_id, ev);
          return;
        }
        for (const h of this.userHandlers) h(ev);
      });
    } catch {
      /* битый кадр или отказ обработчика; /sync изолирует ошибку события и продолжит пачку */
    }
  }

  private isCurrent(client: Client, generation: number): boolean {
    return this.client === client && this.generation === generation;
  }

  private stopTokenTimer() {
    ++this.tokenTimerGeneration;
    if (this.tokenTimer !== null) clearInterval(this.tokenTimer);
    this.tokenTimer = null;
  }

  private startTokenTimer(client: Client, generation: number) {
    this.stopTokenTimer();
    const timerGeneration = this.tokenTimerGeneration;
    const connectedToken = getAccessToken();
    const session = getAuthSession();
    let refreshing = false;
    this.tokenTimer = window.setInterval(() => {
      if (refreshing || timerGeneration !== this.tokenTimerGeneration || !this.isCurrent(client, generation)) return;
      refreshing = true;
      void (async () => {
        const token = getAccessToken();
        if (token === connectedToken && accessExpiresSoon(token)) await tryRefresh();
        if (timerGeneration !== this.tokenTimerGeneration || !this.isCurrent(client, generation) || getAuthSession() !== session) return;
        // WS хранит авторизацию CONNECT: новая пара требует нового соединения.
        if (getAccessToken() !== connectedToken) client.forceDisconnect();
      })().catch(() => {}).finally(() => { refreshing = false; });
    }, 5_000);
  }

  private fallback(client: Client, generation: number) {
    if (!this.isCurrent(client, generation)) return;
    this.stopTokenTimer();
    this.setConnected(false);
    this.startPolling(generation);
  }

  private setConnected(v: boolean) {
    if (this.connected === v) return;
    this.connected = v;
    diag({ kind: 'conn', what: v ? 'ws:connected' : 'ws:disconnected' });
    for (const h of this.statusHandlers) h(v);
  }

  private startPolling(generation: number) {
    if (this.fallbackPollGeneration === generation) return;
    this.stopPolling();
    this.fallbackPollGeneration = generation;
    let failures = 0;
    const tick = async () => {
      if (this.fallbackPollGeneration !== generation || generation !== this.generation || this.connected) return;
      const succeeded = await this.poll(generation);
      if (this.fallbackPollGeneration !== generation || generation !== this.generation || this.connected) return;
      const delayMs = succeeded ? 5_000 : syncRetryDelayMs(failures++, 5_000);
      if (succeeded) failures = 0;
      this.pollTimer = window.setTimeout(() => {
        this.pollTimer = null;
        void tick();
      }, delayMs);
    };
    void tick();
  }

  private stopPolling() {
    if (this.pollTimer !== null) clearTimeout(this.pollTimer);
    this.pollTimer = null;
    this.fallbackPollGeneration = null;
  }

  /**
   * cursor стартует с 0 (не значит "начало времён" — polling ещё ни разу не запускался).
   * Если сразу его не засеять, первый же fallback-poll (например, после обрыва WS при
   * рестарте бэкенда) уйдёт без ?after и получит "хвост" всего updates-лога — старые,
   * уже прочитанные сообщения снова придут как "новые" (звук + бейдж непрочитанного).
   * Здесь только запоминаем текущий курсор, события НЕ обрабатываем — это не новые данные,
   * а просто отметка "докуда история уже учтена" на момент подключения.
   */
  private async seedCursor(generation: number): Promise<boolean> {
    const controller = new AbortController();
    this.seedAbort = controller;
    try {
      const res = await fetch('/api/v1/sync', {
        headers: { Authorization: 'Bearer ' + (getAccessToken() ?? '') },
        signal: controller.signal,
      });
      if (!res.ok || generation !== this.generation) return false;
      const j = (await res.json()) as { cursor: number };
      if (generation !== this.generation) return false;
      this.cursor = Math.max(this.cursor, j.cursor);
      return true;
    } catch {
      /* Подписки уже активны до reconnect catch-up; следующий poll попробует тот же курсор. */
      return false;
    } finally {
      if (this.seedAbort === controller) this.seedAbort = null;
    }
  }

  private async poll(generation = this.generation, retry = true): Promise<boolean> {
    if (generation !== this.generation) return false;
    if (retry) {
      if (this.pollInFlight && this.pollInFlightGeneration === generation) return false;
      this.pollInFlight = true;
      this.pollInFlightGeneration = generation;
    }
    const controller = new AbortController();
    this.pollAbort = controller;
    try {
      // Сервер отдаёт события пачками (до 100) — читаем, пока hasMore, с ограничением на один проход.
      for (let batch = 0; batch < MAX_SYNC_BATCHES; batch++) {
        if (generation !== this.generation) return false;
        const res = await fetch(
          '/api/v1/sync' + (this.cursor ? `?after=${this.cursor}` : ''),
          {
            headers: { Authorization: 'Bearer ' + (getAccessToken() ?? '') },
            signal: controller.signal,
          },
        );
        if (generation !== this.generation) return false;
        if (res.status === 401 && retry && (await tryRefresh())) {
          if (generation === this.generation) return await this.poll(generation, false);
          return false;
        }
        if (!res.ok) return false;
        const j = (await res.json()) as SyncPage;
        if (generation !== this.generation) return false;
        if (j.resync) {
          // Лог обрезан дальше нашего курсора: сначала завершаем полную сверку, затем подтверждаем cursor.
          diag({ kind: 'conn', what: 'sync:resync' });
          const signal = this.lifecycleAbort?.signal;
          if (!signal || signal.aborted) return false;
          for (const h of this.resyncHandlers) {
            await h(signal);
            if (generation !== this.generation || signal.aborted) return false;
          }
          // Верим курсору сервера даже если он меньше нашего (курсор из будущего после сброса Redis).
          this.cursor = j.cursor;
          return true;
        }
        this.applySyncEvents(j);
        if (!j.hasMore) return true;
      }
      return false;
    } catch {
      /* сеть недоступна или поколение уже отменено — следующий тик повторит с того же cursor */
      return false;
    } finally {
      if (this.pollAbort === controller) this.pollAbort = null;
      if (retry && this.pollInFlightGeneration === generation) {
        this.pollInFlight = false;
        this.pollInFlightGeneration = null;
      }
    }
  }

  private applySyncEvents(j: SyncPage) {
    this.cursor = applyBeforeCursor(this.cursor, j.cursor, () => {
      applyEventsIndividually(j.events, (ev) => {
        if (ev.type === 'new_message') {
          if (!ev.message) throw new Error('new_message without message in /sync');
          this.dispatchMessageDto(ev.message);
          return;
        }
        // Остальные типы в /sync содержат chat_id (см. EventPublisher.withChatId).
        const chatId = ev.chat_id ?? ev.message?.chatId;
        if (ev.type && chatId) {
          this.dispatchEventOnce(ev, () => {
            for (const h of this.evtHandlers) h(chatId, ev);
          }, chatId);
        } else if (ev.type) {
          // Персональные события без chat_id — аналог /user/queue/updates.
          this.dispatchEventOnce(ev, () => {
            for (const h of this.userHandlers) h(ev);
          });
        }
      }, (ev, error) => {
        const eventType = typeof ev.type === 'string' ? ev.type.slice(0, 40) : 'unknown';
        const errorType = error instanceof Error ? error.name.slice(0, 40) : 'handler-error';
        diag({ kind: 'conn', what: 'sync:event-handler-error', note: `${eventType}:${errorType}` });
      });
    });
  }
}

export const realtime = new Realtime();
